import { FilterIdsType } from '@app/components/common/FilterSelect/FilterSelect';
import { FieldOverride } from '@app/interfaces/dynamicFields';
import { IFishersFilterOptions } from '@app/interfaces/fisher';
import { tableDataInit } from '@app/pages/FishingOperations/Fishers/constants';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface FishersFilterState {
  overrides: FieldOverride[];
  filterOptions: IFishersFilterOptions;
  shouldTriggerFetch: boolean;
  multiSearch: string;
  sanaSearch: string;
  selectedStatuses: string[];
  selectedStatusesTemp: string[];
  selectedMembershipStatusIds: FilterIdsType;
  selectedMembershipStatusIdsTemp: FilterIdsType;
  selectedMembershipTypeIds: FilterIdsType;
  selectedMembershipTypeIdsTemp: FilterIdsType;
  selectedPaymentDoneIds: FilterIdsType;
  selectedPaymentDoneIdsTemp: FilterIdsType;
  includeSoftDeleted: boolean;
}

const initialState: FishersFilterState = {
  overrides: [],
  filterOptions: tableDataInit.filterOptions,
  shouldTriggerFetch: true,
  multiSearch: '',
  sanaSearch: '',
  selectedStatuses: [],
  selectedStatusesTemp: [],
  selectedMembershipStatusIds: [],
  selectedMembershipStatusIdsTemp: [],
  selectedMembershipTypeIds: [],
  selectedMembershipTypeIdsTemp: [],
  selectedPaymentDoneIds: [],
  selectedPaymentDoneIdsTemp: [],
  includeSoftDeleted: false,
};

const fishersFiltersSlice = createSlice({
  name: 'fishersFilters',
  initialState,
  reducers: {
    setOverrides(state, action: PayloadAction<FieldOverride[]>) {
      state.overrides = action.payload;
    },
    setFilterOptions(state, action: PayloadAction<IFishersFilterOptions>) {
      state.filterOptions = action.payload;
    },
    setShouldTriggerFetch(state, action: PayloadAction<boolean>) {
      state.shouldTriggerFetch = action.payload;
    },
    setMultiSearch(state, action: PayloadAction<string>) {
      state.multiSearch = action.payload;
    },
    setSanaSearch(state, action: PayloadAction<string>) {
      state.sanaSearch = action.payload;
    },
    setSelectedStatuses(state, action: PayloadAction<string[]>) {
      state.selectedStatuses = action.payload;
    },
    setSelectedStatusesTemp(state, action: PayloadAction<string[]>) {
      state.selectedStatusesTemp = action.payload;
    },
    setSelectedMembershipStatusIds(state, action: PayloadAction<FilterIdsType>) {
      state.selectedMembershipStatusIds = action.payload;
    },
    setSelectedMembershipStatusIdsTemp(state, action: PayloadAction<FilterIdsType>) {
      state.selectedMembershipStatusIdsTemp = action.payload;
    },
    setSelectedMembershipTypeIds(state, action: PayloadAction<FilterIdsType>) {
      state.selectedMembershipTypeIds = action.payload;
    },
    setSelectedMembershipTypeIdsTemp(state, action: PayloadAction<FilterIdsType>) {
      state.selectedMembershipTypeIdsTemp = action.payload;
    },
    setSelectedPaymentDoneIds(state, action: PayloadAction<FilterIdsType>) {
      state.selectedPaymentDoneIds = action.payload;
    },
    setSelectedPaymentDoneIdsTemp(state, action: PayloadAction<FilterIdsType>) {
      state.selectedPaymentDoneIdsTemp = action.payload;
    },
    setIncludeSoftDeleted(state, action: PayloadAction<boolean>) {
      state.includeSoftDeleted = action.payload;
      state.shouldTriggerFetch = true;
    },
    resetFilters(state) {
      state.shouldTriggerFetch = true;
      state.multiSearch = initialState.multiSearch;
      state.sanaSearch = initialState.sanaSearch;
      state.selectedStatuses = initialState.selectedStatuses;
      state.selectedStatusesTemp = initialState.selectedStatusesTemp;
      state.selectedMembershipStatusIds = initialState.selectedMembershipStatusIds;
      state.selectedMembershipStatusIdsTemp = initialState.selectedMembershipStatusIdsTemp;
      state.selectedMembershipTypeIds = initialState.selectedMembershipTypeIds;
      state.selectedMembershipTypeIdsTemp = initialState.selectedMembershipTypeIdsTemp;
      state.selectedPaymentDoneIds = initialState.selectedPaymentDoneIds;
      state.selectedPaymentDoneIdsTemp = initialState.selectedPaymentDoneIdsTemp;
      state.includeSoftDeleted = initialState.includeSoftDeleted;
    },
  },
});

export const {
  setOverrides,
  setFilterOptions,
  setShouldTriggerFetch,
  setMultiSearch,
  setSanaSearch,
  setSelectedStatuses,
  setSelectedStatusesTemp,
  setSelectedMembershipStatusIds,
  setSelectedMembershipStatusIdsTemp,
  setSelectedMembershipTypeIds,
  setSelectedMembershipTypeIdsTemp,
  setSelectedPaymentDoneIds,
  setSelectedPaymentDoneIdsTemp,
  setIncludeSoftDeleted,
  resetFilters,
} = fishersFiltersSlice.actions;

export const areFiltersAppliedSelector = (state: { fishersFilters: FishersFilterState }) => {
  const {
    multiSearch,
    sanaSearch,
    selectedStatuses,
    selectedMembershipStatusIds,
    selectedMembershipTypeIds,
    selectedPaymentDoneIds,
    includeSoftDeleted,
  } = state.fishersFilters;

  return (
    !!multiSearch ||
    !!sanaSearch ||
    !!selectedStatuses.length ||
    !!selectedMembershipStatusIds.length ||
    !!selectedMembershipTypeIds.length ||
    !!selectedPaymentDoneIds.length ||
    includeSoftDeleted
  );
};

export const activeFiltersCountSelector = (state: { fishersFilters: FishersFilterState }) => {
  const {
    multiSearch,
    sanaSearch,
    selectedStatuses,
    selectedMembershipStatusIds,
    selectedMembershipTypeIds,
    selectedPaymentDoneIds,
    includeSoftDeleted,
  } = state.fishersFilters;

  let count = 0;
  if (multiSearch) count++;
  if (sanaSearch) count++;
  if (selectedStatuses.length > 0) count++;
  if (selectedMembershipStatusIds.length > 0) count++;
  if (selectedMembershipTypeIds.length > 0) count++;
  if (selectedPaymentDoneIds.length > 0) count++;
  if (includeSoftDeleted) count++;

  return count;
};

export const fetchParamsSelector = (state: { fishersFilters: FishersFilterState }) => {
  const {
    multiSearch,
    sanaSearch,
    selectedStatuses,
    selectedMembershipStatusIds,
    selectedMembershipTypeIds,
    selectedPaymentDoneIds,
    includeSoftDeleted,
  } = state.fishersFilters;

  return {
    multiSearch,
    sanaSearch,
    selectedStatuses,
    selectedMembershipStatusIds,
    selectedMembershipTypeIds,
    selectedPaymentDoneIds,
    includeSoftDeleted,
  };
};

export const fishersFiltersActions = fishersFiltersSlice.actions;

export const fishersFilters = fishersFiltersSlice.reducer;
